import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import socketService from '../../services/socket';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function TenantAlertsTimeline() {
  const { tenantId } = useParams();
  const [alerts, setAlerts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [filters, setFilters] = useState({ severity: '', impact: '' });
  const [noteDrafts, setNoteDrafts] = useState({});

  const loadAlerts = useCallback(() => {
    setStatus('loading');
    return tenantApi
      .listAlerts(tenantId)
      .then((response) => {
        setAlerts(normalizeList(response.data, 'alerts'));
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [tenantId]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    socketService.connect();
    const refreshAlerts = (data) => {
      const tenantId = data?.tenant_id;
      if (tenantId && tenantId !== tenantId) return;
      loadAlerts();
    };
    socketService.on('externalScanCompleted', refreshAlerts);
    return () => {
      socketService.off('externalScanCompleted', refreshAlerts);
    };
  }, [tenantId, loadAlerts]);

  const filteredAlerts = alerts.filter((alert) => {
    const severityMatch = filters.severity
      ? (alert.severity || alert.risk || '').toLowerCase() === filters.severity
      : true;
    const impactMatch = filters.impact
      ? (alert.impact || alert.summary || '').toLowerCase().includes(filters.impact.toLowerCase())
      : true;
    return severityMatch && impactMatch;
  });

  const handleAcknowledge = async (alertId) => {
    try {
      await tenantApi.acknowledgeAlert(tenantId, alertId, { note: noteDrafts[alertId] });
      loadAlerts();
    } catch (error) {
      console.error('Unable to acknowledge alert', error);
    }
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName="Alerts Timeline"
        subtitle="Change events and exposure updates"
      />

      {status === 'loading' && <div className="msp-empty">Loading timeline…</div>}

      {status === 'error' && <div className="msp-empty">Unable to load alerts.</div>}
      {status === 'ready' && filteredAlerts.length === 0 && (
        <div className="msp-empty">No alert activity recorded.</div>
      )}

      <div className="msp-toolbar">
        <select
          className="msp-input"
          value={filters.severity}
          onChange={(event) => setFilters({ ...filters, severity: event.target.value })}
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <input
          type="text"
          className="msp-input"
          placeholder="Filter impact or keyword"
          value={filters.impact}
          onChange={(event) => setFilters({ ...filters, impact: event.target.value })}
        />
      </div>

      {status === 'ready' && filteredAlerts.length > 0 && (
        <div className="msp-timeline">
          {filteredAlerts.map((alert) => (
            <div key={alert.id || alert.alert_id} className="msp-timeline-item">
              <div className="msp-timeline-title">{alert.title || alert.type || 'Change Event'}</div>
              <div className="msp-timeline-meta">
                <span>{alert.timestamp || alert.detected_at}</span>
                <span>{alert.why_it_matters || alert.summary || 'Review impact details.'}</span>
              </div>
              <div className="msp-alert-actions">
                {alert.finding_id && (
                  <a className="msp-link" href={`/msp/tenants/${tenantId}/findings`}>
                    Related Finding
                  </a>
                )}
                {alert.asset_id && (
                  <a className="msp-link" href={`/msp/tenants/${tenantId}/assets`}>
                    Related Asset
                  </a>
                )}
              </div>
              <div className="msp-alert-ack">
                <input
                  type="text"
                  className="msp-input"
                  placeholder="Add analyst note"
                  value={noteDrafts[alert.id || alert.alert_id] || ''}
                  onChange={(event) =>
                    setNoteDrafts({ ...noteDrafts, [alert.id || alert.alert_id]: event.target.value })
                  }
                />
                <button type="button" className="btn btn-secondary" onClick={() => handleAcknowledge(alert.id || alert.alert_id)}>
                  Acknowledge
                </button>
              </div>
              {alert.link && (
                <a href={alert.link} className="msp-link">
                  View details
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TenantAlertsTimeline;
